import { trackEvent, getSessionId } from '../utils/analytics';
import Img from '../components/ui/Img';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight } from 'lucide-react';
import { useBilingualEnabled } from '../config/useBilingualEnabled';
import { useBrandProfile } from '../contexts/brand-profile';
import type { BrandKey } from '../config/brands';

interface FAQItem {
  id: number;
  category: string;
  question: string;
  /** Simplified-Chinese question, shown above the English when bilingual is on. */
  questionZh?: string;
  answer: string | React.ReactNode;
  /** Simplified-Chinese answer, shown above the English when bilingual is on. */
  answerZh?: string | React.ReactNode;
  color: string;
  /**
   * The brands this question is shown under. Omitted means every brand.
   *
   * `['bhcs']` marks a question whose answer is a BHCS policy — Flex, after-school hours, the
   * school-district calendar, transportation, monthly tuition. Those are not just irrelevant to
   * a Kaleido family: each states a rule that is wrong for Kaleido.
   */
  brands?: readonly BrandKey[];
  /**
   * The same question worded for another brand, where the default text speaks as "the school"
   * or names BHCS as the organization. Replaces both languages: the brands that need a
   * rewording are English-only, and the Chinese text would still say the old thing.
   */
  byBrand?: Partial<Record<BrandKey, { question?: string; answer: string | React.ReactNode }>>;
  /**
   * Shown only while the multi-brand portal is on. These explain the profile switcher, and a
   * parent who has no switcher would be reading about a portal they cannot see.
   */
  multiBrandOnly?: boolean;
}

/** Which of `faqs` a brand sees, in that brand's wording. */
function faqsForBrand(faqs: readonly FAQItem[], brand: BrandKey, isMultiBrand: boolean): FAQItem[] {
  return faqs
    .filter(faq => isMultiBrand || !faq.multiBrandOnly)
    .filter(faq => !faq.brands || faq.brands.includes(brand))
    .map(faq => {
      const variant = faq.byBrand?.[brand];
      if (!variant) return faq;
      return {
        ...faq,
        question: variant.question ?? faq.question,
        questionZh: undefined,
        answer: variant.answer,
        answerZh: undefined,
      };
    });
}

/** English category name → Simplified Chinese, for the section headers. */
const CATEGORY_ZH: Record<string, string> = {
  Registration: '注册',
  'Program Profiles': '课程版块',
  Payment: '付款',
  Programs: '课程',
  'BHCS Cash': 'BHCS Cash',
};

/** Search placeholder copy. The bar is narrow, so in bilingual mode we cross-fade
 *  between the two languages rather than showing both at once. */
const SEARCH_PLACEHOLDER_EN = 'Search questions, answers, or categories...';
const SEARCH_PLACEHOLDER_ZH = '搜索问题、答案或分类…';

/** The two buildings programs meet in, as map links. `locations` holds only the room name. */
const mapLink = (address: string) => (
  <a
    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-600 hover:text-blue-800 underline font-medium"
  >
    {address}
  </a>
);
const ANNEX_ADDRESS_LINK = mapLink('1000 E Hillsdale Blvd, Foster City, CA 94404');
const KALEIDO_ROOM_ADDRESS_LINK = mapLink('650 Shell Blvd, Foster City, CA 94404');

export function FAQ() {
  const { user } = useAuth();
  const { activeBrand, isMultiBrand } = useBrandProfile();
  const bilingual = useBilingualEnabled();
  /** Inline bilingual helper for short chrome strings: "中文 English" when on. */
  const bi = (zh: string, en: string) => (bilingual ? `${zh} ${en}` : en);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  // Animated bilingual search placeholder: while the field is empty, cross-fade
  // between Chinese and English so both languages fit the narrow search bar.
  const [phLang, setPhLang] = useState<'zh' | 'en'>('zh');
  const [phVisible, setPhVisible] = useState(true);
  const showAnimatedPlaceholder = bilingual && !searchQuery;
  useEffect(() => {
    if (!showAnimatedPlaceholder) {
      setPhVisible(true);
      return;
    }
    let fadeOut: ReturnType<typeof setTimeout>;
    const cycle = setInterval(() => {
      setPhVisible(false); // fade current language out
      fadeOut = setTimeout(() => {
        setPhLang((prev) => (prev === 'zh' ? 'en' : 'zh'));
        setPhVisible(true); // fade next language in
      }, 350);
    }, 3500);
    return () => {
      clearInterval(cycle);
      clearTimeout(fadeOut);
    };
  }, [showAnimatedPlaceholder]);


  // Handle URL hash navigation to specific FAQ items
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const faqId = parseInt(hash.replace('#faq-', ''));
      if (!isNaN(faqId)) {
        // Expand the specific FAQ item
        setExpandedItems(new Set([faqId]));
        // Scroll to the element
        setTimeout(() => {
          const element = document.getElementById(`faq-${faqId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      trackEvent({
        eventType: 'view_faq',
        user: user,
        sessionId: getSessionId(),
        metadata: {
          page: 'faq',
          timestamp: new Date().toISOString()
        }
      });
    }
  }, [user]);

  // FAQ data structure for search functionality
  const allFaqData: FAQItem[] = useMemo(() => [
    {
      id: 1,
      category: 'Registration',
      question: 'Who can register for the BHCS Portal?',
      questionZh: '谁可以注册 BHCS 平台？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            现已开放注册。任何人都可以使用任意邮箱创建账户，报名我们的夏令营（camps）和兴趣拓展课程（enrichment programs）——无需邀请。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            对于课后班（afterschool）课程，孩子在正式报名前仍需先完成一次校园参观（tour）。校园参观属于报名（enrollment）流程的一部分，而非注册流程，因此您仍可随时创建账户。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            小提示：请使用您常用的邮箱注册——我们会通过该邮箱发送账单、付款确认和课程通知。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
              Registration is now open. Anyone can create an account with any email address to sign up for our camps and enrichment programs — no invitation is required.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
              For afterschool programs, a campus tour is still required before your child can be enrolled. The tour is part of the enrollment process, not registration, so you're welcome to create your account at any time.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
              Tip: Register with an email you check regularly — that's where we'll send invoices, payment confirmations, and program updates.
            </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          question: 'Who can register for the portal?',
          answer: (
            <div>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                Registration is open. Anyone can create an account with any email address and sign up for Kaleido programs — no invitation is required.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                Tip: Register with an email you check regularly — that's where we'll send invoices, payment confirmations, and program updates.
              </p>
            </div>
          ),
        },
      },
      color: 'blue'
    },
    {
      id: 2,
      category: 'Registration',
      question: 'Why didn\'t I receive a confirmation email when registering?',
      questionZh: '注册时为什么没有收到确认邮件？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            我们发现部分邮箱服务商，尤其是自定义域名邮箱（例如 yourname@yourcompany.com），可能会拦截或过滤平台发出的确认邮件。
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">如果您没有看到确认邮件：</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>请检查垃圾邮件文件夹。</li>
            <li>如使用自定义域名邮箱，请联系您的 IT 管理员或邮箱服务商，确认邮件未被拦截或隔离。</li>
            <li>请将发件人加入白名单，或从安全过滤中放行该邮件。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            如问题仍未解决，请通过{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            {' '}联系我们，我们很乐意为您提供进一步帮助。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
              We've noticed that some email providers, especially custom domain emails (e.g., yourname@yourcompany.com), may block or filter the confirmation email from the portal.
            </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">If you don't see the confirmation email:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
              <li>Check your spam or junk folder.</li>
              <li>For custom domain emails, check with your IT administrator or email provider to ensure the message wasn't blocked or quarantined.</li>
              <li>Be sure to whitelist the sender or release the email from any security filters.</li>
            </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
              If the issue persists, please contact us at{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
                billing@bhcskids.com
              </a>
              , and we'll be happy to assist you further.
            </p>
        </div>
      ),
      color: 'yellow'
    },
    {
      id: 3,
      category: 'Registration',
      question: 'How can I add a student to my account?',
      questionZh: '如何将学生添加到我的账户？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            添加学生已支持自助操作——您可以直接在平台中完成，无需联系学校。
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">添加学生：</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>进入“学生（Students）”页面，点击“添加学生（Add Student）”。</li>
            <li>填写孩子的姓名、<strong>出生日期</strong>、就读学校和<strong>当前年级</strong>。</li>
            <li>保存后，即可为孩子报名。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            请务必填写完整、准确的信息。准确的<strong>出生日期</strong>和<strong>年级</strong>能帮助我们为孩子安排合适的项目并适用正确的价格。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            别忘了<strong>签署学生豁免协议（Waiver）</strong>：在孩子的卡片上点击豁免协议链接完成签署。报名前需先签署豁免协议。如需帮助，请联系{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Adding a student is now fully self-serve — you can do it yourself in the portal, no need to email us.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">To add a student:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>Go to the Students page and tap Add Student.</li>
            <li>Enter your child's name, <strong>date of birth</strong>, school, and <strong>current grade</strong>.</li>
            <li>Save, and you're ready to enroll them.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Please fill in complete, accurate details. An accurate <strong>date of birth</strong> and <strong>grade</strong> help us place your child in the right program and apply the correct pricing.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            Don't forget to <strong>sign the student waiver</strong> — tap the waiver link on your child's card to complete it. A signed waiver is required before enrollment. Need a hand? Email us at{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            .
          </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          question: 'How can I add a child to my account?',
          answer: (
            <div>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                Adding a child is self-serve — you can do it yourself in the portal, no need to email us.
              </p>
              <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">To add a child:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
                <li>Go to the Students page and tap Add Student.</li>
                <li>Enter your child's name and <strong>date of birth</strong>.</li>
                <li>Save, and you're ready to enroll them.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                Please use your child's accurate <strong>date of birth</strong> — it helps us place your child in the right program.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                Don't forget to <strong>sign the student waiver</strong> — tap the waiver link on your child's card to complete it. A signed waiver is required before enrollment. Need a hand? Email us at{' '}
                <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
                  billing@bhcskids.com
                </a>
                .
              </p>
            </div>
          ),
        },
      },
      color: 'green'
    },
    {
      id: 4,
      category: 'Registration',
      question: 'Can I add a second guardian so both parents can access enrollment info and process payments?',
      questionZh: '可以添加第二位家长，让两位家长都能查看报名信息并完成付款吗？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            可以！每个家庭最多可有两位家长，且现在邀请共同家长（co-parent）已支持自助操作——您可以直接在平台中完成，无需联系学校。
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">邀请共同家长：</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>进入“学生（Students）”页面。</li>
            <li>在孩子的卡片上，找到“家庭成员（Family Access）”区域，点击“邀请（Invite）”。</li>
            <li>输入对方的邮箱并发送邀请。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            对方会收到一封邀请邮件并完成注册。接受后，即可查看报名信息并进行付款。如对方已有 BHCS 账户，将立即获得访问权限。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Yes! Each family can have up to two parents, and inviting a co-parent is now self-serve — you can do it yourself in the portal, no need to contact the school.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">To invite a co-parent:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>Go to the Students page.</li>
            <li>On your child's card, find the Family Access section and tap Invite.</li>
            <li>Enter the co-parent's email and send the invitation.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            They'll receive an email invitation to register. Once they accept, they can view enrollment info and process payments. If they already have a BHCS account, access is granted right away.
          </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <div>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                Yes! Each family can have up to two parents, and inviting a co-parent is self-serve — you can do it yourself in the portal, no need to contact us.
              </p>
              <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">To invite a co-parent:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
                <li>Go to the Students page.</li>
                <li>On your child's card, find the Family Access section and tap Invite.</li>
                <li>Enter the co-parent's email and send the invitation.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed text-sm">
                They'll receive an email invitation to register. Once they accept, they can view enrollment info and process payments. If they already have a portal account, access is granted right away.
              </p>
            </div>
          ),
        },
      },
      color: 'purple'
    },
    {
      id: 33,
      category: 'Program Profiles',
      question: 'What are the BHCS and Kaleido program profiles?',
      questionZh: 'BHCS 和 Kaleido 两个课程版块（program profile）是什么？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            平台包含两个课程版块：BHCS（Bright Horizon Chinese School）和 Kaleido（面向 2–5 岁儿童的早期教育）。两者使用同一个账户，无需另外注册。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            页面顶部的 BHCS / Kaleido 按钮显示您当前所在的版块，点击即可切换。切换只会改变您浏览的课程，以及“主页（Dashboard）”上显示的报名；不会为您报名、退课或产生任何费用。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            您的孩子、BHCS Cash 和付款记录在两个版块之间共享。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            The portal has two program profiles: BHCS (Bright Horizon Chinese School) and Kaleido (early learning for ages 2–5). You use the same account for both — there is nothing extra to sign up for.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            The BHCS / Kaleido button at the top of the page shows which profile you are in; tap it to switch. Switching only changes which programs you browse and which enrollments your Dashboard shows. It never enrolls you, unenrolls you, or charges you.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            Your children, BHCS Cash and payment history are shared across both profiles.
          </p>
        </div>
      ),
      multiBrandOnly: true,
      color: 'indigo'
    },
    {
      id: 34,
      category: 'Program Profiles',
      question: 'Why is an enrollment or invoice missing from my Dashboard?',
      questionZh: '为什么我的“主页（Dashboard）”上看不到某个报名或账单？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-2 text-sm">
            “主页（Dashboard）”和“课程（Programs）”页面只显示您当前所在的版块，因此在 BHCS 版块中看不到 Kaleido 的课程，反之亦然。您的信息并没有丢失：
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 text-sm">
            <li>“账单（Payments）”页面列出两个版块的全部账单，每张都标有 BHCS 或 Kaleido，您可以在那里支付任意一张。</li>
            <li>“学生（Students）”页面显示每个孩子在两个版块中的全部报名。</li>
            <li>您也可以点击页面顶部的 BHCS / Kaleido 按钮切换版块。</li>
          </ul>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-2 text-sm">
            The Dashboard and Programs pages show only the profile you are in, so a Kaleido class does not appear there while you are in BHCS, and the other way around. Nothing is lost:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 text-sm">
            <li>Payments lists invoices from both profiles, each labeled BHCS or Kaleido, and you can pay any of them there.</li>
            <li>Students shows every enrollment for each child, in both profiles.</li>
            <li>Or switch profiles with the BHCS / Kaleido button at the top of the page.</li>
          </ul>
        </div>
      ),
      multiBrandOnly: true,
      color: 'indigo'
    },
    {
      id: 35,
      category: 'Program Profiles',
      question: 'Why did the portal switch profiles on its own?',
      questionZh: '为什么平台自动切换了版块？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-2 text-sm">
            以下两种情况会为您选择版块：
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>从平台以外打开的链接（例如账单邮件，或别人分享给您的课程链接），会直接进入该内容所属的版块。</li>
            <li>平台会记住您上次使用的版块，并同步到您登录的所有设备。例如您在电脑上切换后，手机也会打开同一个版块。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            您可以随时点击页面顶部的 BHCS / Kaleido 按钮切换回来。切换不会改变您的报名或付款。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-2 text-sm">
            Two things choose a profile for you:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>A link opened from outside the portal — an invoice email, or a program link someone shared — opens the profile that invoice or program belongs to.</li>
            <li>The portal remembers the last profile you used, on every device you sign in on. If you switch on your computer, your phone opens in that profile too.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            You can switch back at any time with the BHCS / Kaleido button at the top of the page. Switching never changes your enrollments or payments.
          </p>
        </div>
      ),
      multiBrandOnly: true,
      color: 'indigo'
    },
    {
      id: 6,
      category: 'Payment',
      question: 'Why is there a limit on my Zelle payment?',
      questionZh: '为什么我的 Zelle 付款有限额？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Zelle 付款可能受每日或单笔限额限制，这些限额由您的银行或金融机构设定。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            多数情况下，在多次成功交易后，这些限额可能会自动提高。如需了解您的具体 Zelle 限额或申请提额，请直接联系您的银行。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            如在支付账单时遇到限额，您可以将总金额拆分为多笔 Zelle 付款来完成全额支付。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
              Zelle payments may be subject to daily or per-transaction limits, which are determined by your bank or financial institution. 
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            In many cases, these limits may be increased automatically after several successful transactions. For more details about your specific Zelle limits or to request an increase, please contact your bank directly.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
              If you encounter a payment limit while trying to pay your invoice, you may split the total amount into multiple Zelle payments to complete the full payment.
            </p>
        </div>
      ),
      color: 'blue'
    },
    {
      id: 7,
      category: 'Payment',
      question: 'Why does my payment status show as "Pending" after using ACH?',
      questionZh: '使用 ACH 后，为什么我的付款状态显示为“待处理（Pending）”？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed text-sm">
            通过 ACH（银行转账）支付时，状态可能显示为“待处理（Pending）”，因为银行通常需要 3–5 个工作日来完成交易处理。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            付款清算后，状态会自动更新为“已付（Paid）”。感谢您在处理期间的耐心等待。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed text-sm">
              Payments made via ACH (bank transfer) may show a "Pending" status because it typically takes 3–5 business days for the bank to fully process the transaction. 
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
              Once the payment clears, the status will automatically update to "Paid." We appreciate your patience during this processing period.
            </p>
        </div>
      ),
      color: 'indigo'
    },
    {
      id: 8,
      category: 'Payment',
      question: 'Can I pay all invoices at once instead of making separate payments for each one?',
      questionZh: '可以一次性支付所有账单，而不必逐张支付吗？',
      answerZh: (
        <div data-testid="faq-bundle-payment">
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            可以。在“账单（Payments）”页面的“账单”标签下，会显示<strong>待缴费用</strong>和<strong>结账</strong>按钮。结账页面列出所有待缴账单，并已全部勾选；暂时不想支付的账单，取消勾选即可。然后用信用卡、银行账户（ACH）或 Zelle 一次付清。
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 text-sm">
            <li><strong>信用卡或银行账户：</strong>所有勾选的账单合为一笔扣款，只发一封收据邮件。信用卡付款含手续费，付款前会显示。银行（ACH）付款需 3–5 个工作日处理，期间账单显示为“Pending”。</li>
            <li><strong>Zelle：</strong>整笔付款使用同一个备注码（memo code）。请将总金额转账至 billing@bhcskids.com，备注栏只填写该备注码。如银行有每日转账限额，可分几天用同一备注码转账，备注码 14 天内有效。如只转了部分金额，会尽量按整张账单入账，先付最早的账单；超出账单应付金额的部分会存入您的 BHCS Cash。</li>
            <li><strong>BHCS Cash 和优惠券</strong>需逐张账单使用：在账单上选择<strong>单独缴费</strong>，即可像以前一样单独支付。</li>
          </ul>
        </div>
      ),
      answer: (
        <div data-testid="faq-bundle-payment">
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Yes. On the <strong>Payments</strong> page, the Invoices tab shows what is <strong>Due now</strong> with a{' '}
            <strong>Checkout</strong> button. Checkout lists every invoice that is due, already ticked; untick any you want to
            leave for later, then pay the rest at once by card, bank account (ACH), or Zelle.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 text-sm">
            <li><strong>Card or bank account:</strong> one charge for all the ticked invoices, and one receipt email. Card payments include a processing fee, shown before you pay. Bank (ACH) payments take 3–5 business days to clear, and the invoices show &quot;Pending&quot; until then.</li>
            <li><strong>Zelle:</strong> you get one memo code for the whole payment. Send the total to billing@bhcskids.com with only that code in the memo. If your bank limits daily transfers, send it over several days with the same code; it works for 14 days. If you send part of the total, it is applied to whole invoices where possible, oldest first. Anything beyond what the invoices owe is added to your BHCS Cash.</li>
            <li><strong>BHCS Cash and coupons</strong> are used one invoice at a time: choose <strong>Pay only this</strong> on an invoice to pay it on its own, as before.</li>
          </ul>
        </div>
      ),
      color: 'emerald'
    },
    {
      id: 9,
      category: 'Payment',
      question: 'Where can I view my payment history and download invoices?',
      questionZh: '在哪里查看付款记录并下载账单？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            您可以在平台的“账单（Payments）”页面找到所有历史账单。
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">查看或下载账单：</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 text-sm">
            <li>进入“账单（Payments）”页面。</li>
            <li>点击您想查看的账单。</li>
            <li>在账单页面右上角，您会看到下载可打印版本的选项，方便您留存以备个人或报税之需。</li>
          </ul>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            You can find all your past invoices on the Payment page of the portal.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">To view or download an invoice:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 text-sm">
              <li>Go to the Payment page.</li>
              <li>Click on the invoice you wish to view.</li>
              <li>In the top right corner of the invoice page, you'll see an option to download a printable version. This allows you to keep a record for your personal or tax-related needs.</li>
            </ul>
          </div>
      ),
      color: 'amber'
    },
    {
      id: 10,
      category: 'Payment',
      question: 'How do I make a Zelle payment?',
      questionZh: '如何进行 Zelle 付款？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            进行 Zelle 付款，请按以下简单步骤操作：
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li><strong>打开您的银行 App</strong>（Chase、Bank of America、Wells Fargo 等），进入 Zelle 板块</li>
            <li><strong>输入收款邮箱：</strong>billing@bhcskids.com</li>
            <li><strong>输入付款金额</strong>（账单上显示的金额）</li>
            <li><strong>输入准确的备注码</strong>，填入“留言/给收款人的备注（Message/Memo to Recipient）”栏——这对将您的付款匹配到正确账单至关重要。请勿在备注中添加课程名称或学生姓名，备注码才是匹配付款的关键。</li>
            <li><strong>核对并发送</strong>付款</li>
          </ul>

          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2 text-sm">示例：Wells Fargo Zelle 付款</h4>
            <div className="relative">
              <Img
                src="/assets/screenshots/wellsfargo-zelle.jpeg"
                alt="Wells Fargo Zelle payment example showing memo field with BEAUTIFUL99 highlighted"
                className="w-full max-w-sm mx-auto rounded-lg shadow-sm border border-gray-300"
                width={750}
                height={1334}
                loading="lazy"
              />
              <div className="text-xs text-gray-600 mt-2 text-center">
                <strong>注意：</strong>备注栏（红色高亮处）填写的是 “BEAUTIFUL99”——这里就是您输入账单备注码的位置
              </div>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed text-sm">
            <strong>重要提示：</strong>进行 Zelle 付款时，请务必输入准确的备注码。这有助于我们将您的付款匹配到正确的账单，并及时更新您的账户状态。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            如对 Zelle 付款有任何疑问，请通过{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            {' '}联系我们。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            To make a Zelle payment, follow these simple steps:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li><strong>Open your banking app</strong> (Chase, Bank of America, Wells Fargo, etc.) and navigate to the Zelle section</li>
            <li><strong>Enter the recipient email:</strong> billing@bhcskids.com</li>
            <li><strong>Enter the payment amount</strong> shown on your invoice</li>
            <li><strong>Enter the exact memo code</strong> in the "Message/Memo to Recipient" field - this is crucial for matching your payment to the correct invoice. Do not add program name or student name to the memo, the memo code is the key to matching your payment to the correct invoice.</li>
            <li><strong>Review and send</strong> the payment</li>
          </ul>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2 text-sm">Example: Wells Fargo Zelle Payment</h4>
            <div className="relative">
              <Img 
                src="/assets/screenshots/wellsfargo-zelle.jpeg" 
                alt="Wells Fargo Zelle payment example showing memo field with BEAUTIFUL99 highlighted" 
                className="w-full max-w-sm mx-auto rounded-lg shadow-sm border border-gray-300"
                width={750}
                height={1334}
                loading="lazy"
              />
              <div className="text-xs text-gray-600 mt-2 text-center">
                <strong>Note:</strong> The memo field (highlighted in red) contains "BEAUTIFUL99" - this is where you enter your invoice memo code
              </div>
            </div>
          </div>
          
          <p className="text-gray-700 leading-relaxed text-sm">
            <strong>Important:</strong> Always enter the exact memo code when making your Zelle payment. This helps us match your payment to the correct invoice and update your account status promptly.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            If you have any questions about Zelle payments, please contact us at{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            .
          </p>
        </div>
      ),
      color: 'blue'
    },
    {
      id: 11,
      category: 'Payment',
      question: 'Why am I seeing a "Load failed" message when setting up a bank account for ACH payment?',
      questionZh: '设置 ACH 付款的银行账户时，为什么会出现“Load failed（加载失败）”的提示？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            如果您在金融验证服务（例如我们支付服务商所使用的服务）受限或被屏蔽的地区尝试关联银行账户，可能会出现此错误——最常见于中国大陆。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            这些服务依赖安全的美国本地网络，可能因地区网络限制而无法完全加载。
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">如遇此问题：</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>建议使用其他支付方式，例如 Zelle。</li>
            <li>您也可以尝试使用 VPN 或通过其他网络访问平台。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            如需进一步协助或其他付款安排，请通过{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            {' '}联系我们，我们很乐意提供帮助。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            This error may occur if you're trying to link your bank account from a region where financial verification services (such as those used by our payment provider) are restricted or blocked — most commonly in mainland China.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            These services rely on secure U.S.-based networks that may not fully load due to regional internet limitations.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">If you encounter this issue:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>We recommend using an alternative payment method such as Zelle.</li>
            <li>You may also try accessing the portal using a VPN or from a different network.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            For further assistance or alternative payment arrangements, please contact us at{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            {' '}and we'll be happy to help.
          </p>
        </div>
      ),
      color: 'indigo'
    },
    {
      id: 12,
      category: 'Payment',
      question: 'How do I set up Auto Pay?',
      questionZh: '如何设置自动付款（Auto Pay）？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            启用自动付款：
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>进入<strong>账户（Account）</strong>页面。</li>
            <li>如尚未添加，请添加<strong>银行账户（ACH）</strong>或<strong>银行卡（Card）</strong>。</li>
            <li>在<strong>支付方式（Payment Methods）</strong>下，将默认方式设为<strong>银行账户</strong>或<strong>银行卡</strong>。</li>
            <li>将<strong>自动付款（Auto Pay）</strong>开关打开。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            注意：自动付款<strong>不支持 Zelle</strong>。启用后，自动付款将应用于<strong>按月循环（recurring monthly）</strong>课程的账单，并在账单未付时自动扣款您的默认银行账户或银行卡。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            To enable Auto Pay:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>Go to the <strong>Account</strong> page.</li>
            <li>Add a <strong>Bank Account (ACH)</strong> or a <strong>Card</strong> if you haven’t already.</li>
            <li>Under <strong>Payment Methods</strong>, set your default to <strong>Bank Account</strong> or <strong>Card</strong>.</li>
            <li>Toggle <strong>Auto Pay</strong> ON.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            Note: Auto Pay is <strong>not available for Zelle</strong>. When enabled, Auto Pay applies to invoices for <strong>recurring monthly</strong> programs and will automatically charge your default bank account or card when invoices are outstanding.
          </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'blue'
    },
    {
      id: 13,
      category: 'Programs',
      question: 'What is the Flex afterschool program, and how is tuition calculated?',
      questionZh: '什么是课后班按月自选（Flex）课程？学费是怎么计算的？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            课后班按月自选（Flex）课程让您每月自由选择孩子的上课日期，而非固定课表。不再有固定的 2 天、3 天或 4 天要求——您可以为当月安排具体的上课日期。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            学费按月计算，依据您所安排的天数：<strong>当月确认天数 × 每日单价</strong>。一般建议每周选择约 1–5 天。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            材料费在报名时一次性收取；任何固定月费按全额收取。优惠将作用于您当月的总金额。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            The Flex afterschool program lets you choose which days your child attends each month, instead of a fixed schedule. There's no set 2-, 3-, or 4-day requirement — you build a plan of specific dates for the month.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Tuition is billed monthly based on the days you plan: <strong>number of confirmed days × the per-day rate</strong>. As a general guide, families choose about 1–5 days per week.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            Materials are a one-time charge at enrollment, and any fixed monthly fees are billed in full. Discounts apply to your computed monthly total.
          </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'purple'
    },
    {
      id: 14,
      category: 'Programs',
      question: 'How do I choose or change my Flex days each month?',
      questionZh: '每个月如何选择或修改按月自选（Flex）课程的上课日期？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            第一个月的上课日期在报名时设置，并会实时预览费用。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            之后的每个月，进入“学生（Students）”或“主页（Dashboard）”→ 您的按月自选课程 → “报名详情（Enrollment Details）”→ 按月自选计划区域，勾选您想要的日期并保存。系统会随之实时更新预计费用。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            您可以在<strong>锁定日</strong>之前修改下个月的计划——锁定日为<strong>当月 15 日</strong>。15 日之后，下个月的计划将被冻结，并根据已确认的日期生成账单。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            如果您在 15 日之后报名，已被锁定的下个月将由管理团队为您设置；再下个月起您即可自行修改。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Your first month's days are set when you enroll, with a live preview of the cost.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            For each following month, go to the Students or Dashboard page → your Flex enrollment → Enrollment Details → the Flex plan section, toggle the days you want, and Save. You'll see the projected cost update as you go.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            You can edit next month's plan until the <strong>lock day — the 15th of the current month</strong>. After the 15th, next month's plan is frozen and its invoice is generated from the confirmed days.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            If you enroll after the 15th, the already-locked next month is set by our admin team; you'll be able to edit the month after that.
          </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'indigo'
    },
    {
      id: 15,
      category: 'Programs',
      question: 'With Flex, what happens if my child is absent or I want to skip a month?',
      questionZh: '按月自选（Flex）课程中，如果孩子缺勤或我想暂停一个月会怎样？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            按月自选课程<strong>按计划计费，而非按出勤计费</strong>。已确认计划中的某一天，无论孩子是否到课都会计费；已锁定月份中的病假或缺勤<strong>不会自动退费</strong>。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            如您已知某天无法到课，请在 <strong>15 日锁定前</strong>从计划中删除该日期。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            您可以<strong>暂停一个月</strong>：某月计划为 0 天时，当月不会生成账单，且您的报名在后续月份继续有效。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            已锁定月份中有不可避免的缺勤需要退费？请发送邮件至{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            ——善意退费由管理团队逐案处理。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Flex is <strong>billed on your plan, not on attendance</strong>. A day on your confirmed plan is billed whether or not your child attends, and a sick day or absence in an already-locked month is <strong>not automatically credited</strong>.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            To avoid being charged for a day you know you'll miss, remove that date from the plan <strong>before the 15th lock</strong>.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            You can <strong>pause for a month</strong>: a plan with 0 days generates no invoice that month, and your enrollment stays active for future months.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            Need a credit for an unavoidable absence in a locked month? Email{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
              billing@bhcskids.com
            </a>
            {' '}— goodwill credits are handled case-by-case by our admin team.
          </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'teal'
    },
    {
      id: 16,
      category: 'Programs',
      question: 'What are the hours of the afterschool program?',
      questionZh: '课后班的时间是怎样的？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            课后班从学校放学起一直持续到下午 6:00。
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>如果孩子早上未到校上学且无需接送，请尽早告知我们，以便我们做出相应安排。</li>
            <li>如果孩子因病缺席，恳请让孩子在家休息，以帮助我们为所有学生和教职员工维护安全健康的环境。</li>
            <li>对于希望自行将孩子送到课后班的家庭，中午 12:00 之后随时可以送达。</li>
            <li>接孩子时间不得晚于下午 6:00。最晚接送时间为下午 6:20，之后将按分钟收取迟到费。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            感谢您的配合，帮助我们为所有学生提供顺畅、安全的课程。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            The afterschool program runs from school dismissal until 6:00 PM.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
              <li>If your child is absent from morning school and does not require pickup, please inform us as soon as possible so we can make appropriate arrangements.</li>
              <li>If your child is absent due to illness, we kindly ask that you keep them at home to help us maintain a safe and healthy environment for all students and staff.</li>
              <li>For families who want to drop off their child directly to the afterschool program, drop-off is available any time after 12:00 PM.</li>
              <li>Pick-up time is no later than 6:00 PM. The latest pick-up allowed is 6:20 PM, after which a late fee will be charged per minute.</li>
            </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            Thank you for your cooperation in helping us provide a smooth and safe program for all students.
          </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'cyan'
    },
    {
      id: 17,
      category: 'Programs',
      question: 'Will tuition be prorated for shortened months?',
      questionZh: '课时缩短的月份学费会按比例计算吗？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            会。以下月份的学费会按比例计算：8 月、11 月、12 月和 6 月。
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            这些月份的账单会在付款时自动反映调整后的学费。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            付款前，您可以点击“查看账单（View Invoice）”，查看总金额中所含按比例计费项目的详细明细。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
              Yes. Tuition will be prorated for months for: August, December and June. 
          </p>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
              Your invoice for these months will automatically reflect the adjusted tuition at the time of payment. 
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
              You can click "View Invoice" before making a payment to see a detailed breakdown of the prorated components included in the total amount.
            </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'green'
    },
    {
      id: 18,
      category: 'Programs',
      question: 'If my child\'s school does not follow the San Mateo Foster City (SMFC) School District calendar, will I pay the same tuition as SMFC students?',
      questionZh: '如果我孩子的学校不采用 San Mateo Foster City（SMFC）学区校历，我支付的学费会和 SMFC 学生一样吗？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed text-sm">
            学费通常依据 SMFC 学区校历，对课时缩短的月份（8 月、11 月、12 月和 6 月）按比例计算。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            但如果您孩子学校的校历有不同的缩短月份，或这些月份的上课天数不同，您的账单会相应调整，以反映您孩子学校的具体校历。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed text-sm">
              Tuition is typically prorated for shortened months (August, December, and June) based on the SMFC school district calendar. 
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            However, if your student's school calendar has different shortened months or a different number of school days during those months, your invoice will be adjusted accordingly to reflect your student's specific school schedule.
          </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'orange'
    },
    {
      id: 19,
      category: 'Programs',
      question: 'Can I drop off my student myself instead of using BHCS transportation?',
      questionZh: '我可以自己接送孩子，而不使用 BHCS 的交通服务吗？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            可以，欢迎您<strong>自行安排</strong>从孩子早上学校到 BHCS 校区的交通。
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">如您选择自行接送，请：</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>在报名时与我们的工作人员说明。</li>
            <li>在平台为孩子报名时取消勾选交通选项。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            这样可确保每月交通费不会计入您的月度账单。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Yes, you are welcome to <strong>arrange your own transportation</strong> from your child's morning school to the BHCS campus.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-medium text-sm">If you choose to do so, please:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
              <li>Let us know during registration when speaking with our staff.</li>
              <li>Uncheck the transportation option when enrolling your student in the portal.</li>
            </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            This ensures that the monthly transportation fee is not included in your monthly invoice.
          </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'teal'
    },
    {
      id: 20,
      category: 'Programs',
      question: 'What is the refund policy if I withdraw in the middle of the school year?',
      questionZh: '如果我在学年中途退学，退款政策是怎样的？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            如您在学年中途为孩子退学，请注意：
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>不满整月的学费及材料费不予退还。</li>
            <li>仅在我们提前收到 30 天书面退学通知的情况下，才会退还下一个月的学费。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            请提前规划，并尽早通知我们，以避免不必要的费用。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            If you withdraw your student during the school year, please note:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
              <li>Partial month tuition and material fees are non-refundable.</li>
              <li>Tuition will only be refunded for the following month, provided we receive a 30-day written withdrawal notice in advance.</li>
            </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            Please plan accordingly and notify us as early as possible to avoid unnecessary charges.
          </p>
        </div>
      ),
      brands: ['bhcs'],
      color: 'rose'
    },
    {
      id: 36,
      category: 'Programs',
      question: 'Where are the Annex and the Kaleido Room?',
      questionZh: 'Annex 和 Kaleido Room 在哪里？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            两个教室位于不同的建筑内：
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li><strong>Annex</strong>：Foster City Community Center Annex，{ANNEX_ADDRESS_LINK}。位于 Foster City 图书馆楼上（顶层），主要用于 1–5 年级的课程。</li>
            <li><strong>Kaleido Room</strong>：新落成的 Foster City Community Center，{KALEIDO_ROOM_ADDRESS_LINK}。位于 Leo J. Ryan 公园内（2026 年 9 月开放），主要用于 TK、K 及 Kaleido 课程。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            标注为“Annex &amp; Kaleido Room”的营地会同时使用两处场地。每个课程的上课地点都显示在平台的课程信息中。如有疑问，请联系{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">billing@bhcskids.com</a>。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            They're in two different buildings:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li><strong>Annex</strong>: Foster City Community Center Annex, {ANNEX_ADDRESS_LINK}. Top floor, upstairs from the Foster City Library. Used for grades 1–5 programs.</li>
            <li><strong>Kaleido Room</strong>: the new Foster City Community Center, {KALEIDO_ROOM_ADDRESS_LINK}. In Leo J. Ryan Park, opened September 2026. Used for TK &amp; K and Kaleido programs.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            Camps listed as &ldquo;Annex &amp; Kaleido Room&rdquo; use both buildings. Your child&rsquo;s room is shown on each program in the portal. Questions? Email{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">billing@bhcskids.com</a>.
          </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          question: 'Where is the Kaleido Room?',
          answer: (
            <div>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                The Kaleido Room is in the new Foster City Community Center, {KALEIDO_ROOM_ADDRESS_LINK}, in Leo J. Ryan Park.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                Each program shows where it meets. Questions? Email{' '}
                <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">billing@bhcskids.com</a>.
              </p>
            </div>
          ),
        },
      },
      color: 'amber'
    },
    {
      id: 5,
      category: 'Registration',
      question: 'How do I unenroll my student from a program?',
      questionZh: '如何为孩子退选某个课程？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            为孩子退选课程，请按以下步骤操作：
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>进入“学生（Students）”页面，点击该学生名下列出的课程。</li>
            <li>或进入“主页（Dashboard）”，在“当前报名（Active Enrollments）”区域找到该课程并点击。</li>
            <li>在课程页面您会看到“退课（Unenroll）”按钮——点击即可完成退选。</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            如您无法成功退选，欢迎联系我们，管理员会很乐意协助您完成。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            To unenroll your student from a program, follow these steps:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3 text-sm">
            <li>Go to the Students page and click on the program listed under the student's block.</li>
            <li>Or, navigate to the Dashboard, locate the program under the Active Enrollments section, and click on it.</li>
            <li>You should see an Unenroll button on the program page—click it to complete the process.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed text-sm">
            If you're unable to unenroll successfully, feel free to reach out and an admin will be happy to assist you with the process.
          </p>
        </div>
      ),
      color: 'green'
    },
    {
      id: 21,
      category: 'BHCS Cash',
      question: 'What is BHCS Cash?',
      questionZh: '什么是 BHCS Cash？',
      answerZh: (
        <p className="text-gray-700 leading-relaxed text-sm">
          BHCS Cash 是您可以预先充值到 BHCS 账户中的余额，用于支付学费和各项费用。可以把它理解为一个随时备用、账单到来即可使用的校园账户。
        </p>
      ),
      answer: (
        <p className="text-gray-700 leading-relaxed text-sm">
          BHCS Cash is a pre-funded balance you can add to your BHCS account and use to pay tuition and fees. Think of it like a school account that&apos;s ready when your invoice arrives.
        </p>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <p className="text-gray-700 leading-relaxed text-sm">
              BHCS Cash is a pre-funded balance you can add to your portal account and use to pay for programs. One balance covers invoices from both Kaleido and Bright Horizon Chinese School, which is where the name comes from. Think of it like a family account that&apos;s ready when your invoice arrives.
            </p>
          ),
        },
      },
      color: 'emerald'
    },
    {
      id: 22,
      category: 'BHCS Cash',
      question: 'How do I add funds to BHCS Cash?',
      questionZh: '如何为 BHCS Cash 充值？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            进入“账单（Payments）→ BHCS Cash”，点击“充值（Add Funds）”。您可以使用信用卡/借记卡、ACH 银行转账或 Zelle 支付。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            当金额与您的申请一致时，Zelle 转账会自动确认。如您发送的金额与申请不符，学校会与您联系以解决差额。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Go to Payments &rarr; BHCS Cash and tap &quot;Add Funds.&quot; You can pay by credit/debit card, ACH bank transfer, or Zelle.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            Zelle transfers are confirmed automatically when the amount matches your request. If you send a different amount than requested, the school will contact you to resolve the difference.
          </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <div>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                Go to Payments &rarr; BHCS Cash and tap &quot;Add Funds.&quot; You can pay by credit/debit card, ACH bank transfer, or Zelle.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                Zelle transfers are confirmed automatically when the amount matches your request. If you send a different amount than requested, we will contact you to resolve the difference.
              </p>
            </div>
          ),
        },
      },
      color: 'teal'
    },
    {
      id: 23,
      category: 'BHCS Cash',
      question: 'Do I get a bonus for adding funds?',
      questionZh: '充值会有赠送吗？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            会——BHCS 目前对 BHCS Cash 充值提供赠送。例如，充值 $100 可额外获得赠送额度。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            赠送部分为不可提现额度，且不能以现金形式退还。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Yes &mdash; BHCS currently offers a bonus on BHCS Cash purchases. For example, add $100 and receive bonus credit on top.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            The bonus portion is a non-withdrawable credit and cannot be refunded as cash.
          </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <div>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                Yes &mdash; we currently offer a bonus on BHCS Cash purchases. For example, add $100 and receive bonus credit on top.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                The bonus portion is a non-withdrawable credit and cannot be refunded as cash.
              </p>
            </div>
          ),
        },
      },
      color: 'amber'
    },
    {
      id: 24,
      category: 'BHCS Cash',
      question: 'What is the difference between withdrawable and non-withdrawable balance?',
      questionZh: '可提现余额与不可提现余额有什么区别？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            <strong>可提现余额</strong>是您自己充值的金额——如不再需要，可以申请退回。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            <strong>不可提现余额</strong>是学校赠予的额度（奖学金、促销、赠送等）——只能用于支付账单，不能兑换成现金。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            <strong>Withdrawable balance</strong> is money you added yourself &mdash; you can get it back if you no longer need it.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            <strong>Non-withdrawable balance</strong> is credit granted by the school (scholarships, promotions, bonuses) &mdash; it can only be used to pay invoices and cannot be converted to cash.
          </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <div>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                <strong>Withdrawable balance</strong> is money you added yourself &mdash; you can get it back if you no longer need it.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                <strong>Non-withdrawable balance</strong> is credit we grant (scholarships, promotions, bonuses) &mdash; it can only be used to pay invoices and cannot be converted to cash.
              </p>
            </div>
          ),
        },
      },
      color: 'indigo'
    },
    {
      id: 25,
      category: 'BHCS Cash',
      question: 'Which type of balance gets used first when I pay an invoice?',
      questionZh: '支付账单时会优先使用哪种余额？',
      answerZh: (
        <p className="text-gray-700 leading-relaxed text-sm">
          系统始终优先使用不可提现额度，然后再使用可提现余额。这样可以最大化学校赠予额度的价值。
        </p>
      ),
      answer: (
        <p className="text-gray-700 leading-relaxed text-sm">
          Non-withdrawable credit is always applied first, then your withdrawable balance. This maximizes the value of school-granted credits.
        </p>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <p className="text-gray-700 leading-relaxed text-sm">
              Non-withdrawable credit is always applied first, then your withdrawable balance. This maximizes the value of the credits we grant.
            </p>
          ),
        },
      },
      color: 'cyan'
    },
    {
      id: 26,
      category: 'BHCS Cash',
      question: 'What happens if my BHCS Cash doesn\'t have enough to cover an invoice?',
      questionZh: '如果我的 BHCS Cash 不足以支付账单怎么办？',
      answerZh: (
        <div className="text-gray-700 leading-relaxed text-sm space-y-2">
          <p>
            如您有已保存的银行卡或银行账户，系统会先使用 BHCS Cash，余额不足部分将自动扣款（拆分付款）。
          </p>
          <p>
            如您仅使用 Zelle，仍可两者结合：先用 Zelle 支付余额<em>未</em>覆盖的部分，然后回到账单，用 BHCS Cash 支付其余金额。您的 Zelle 付款确认后，余额将正好覆盖剩余金额。
          </p>
        </div>
      ),
      answer: (
        <div className="text-gray-700 leading-relaxed text-sm space-y-2">
          <p>
            If you have a saved card or bank account, your BHCS Cash will be applied first and the remainder charged automatically (split payment).
          </p>
          <p>
            If you only use Zelle, you can still combine both: first send a Zelle payment for the amount <em>not</em> covered by your balance, then return to the invoice and pay the rest with BHCS Cash. Once your Zelle payment is confirmed, your balance will cover the remaining amount exactly.
          </p>
        </div>
      ),
      color: 'orange'
    },
    {
      id: 27,
      category: 'BHCS Cash',
      question: 'Can I combine BHCS Cash with Zelle if I don\'t have a card on file?',
      questionZh: '如果我没有绑定银行卡，可以将 BHCS Cash 与 Zelle 结合使用吗？',
      answerZh: (
        <div className="text-gray-700 leading-relaxed text-sm space-y-2">
          <p>可以——方法如下：</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>打开账单，记下应付总额和当前 BHCS Cash 余额。</li>
            <li>计算差额：<em>账单总额 − BHCS Cash 余额</em>。</li>
            <li>用您的账单备注码，将该差额作为 Zelle 付款发送。</li>
            <li>学校确认您的 Zelle 付款后，回到该账单。</li>
            <li>选择 <strong>BHCS Cash</strong> 作为支付方式——余额此时即可全额覆盖剩余金额。</li>
          </ol>
          <p>
            这样即使您更偏好 Zelle 而非银行卡或银行转账，也能使用 BHCS Cash。
          </p>
        </div>
      ),
      answer: (
        <div className="text-gray-700 leading-relaxed text-sm space-y-2">
          <p>Yes &mdash; here is how:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open the invoice and note the total due and your current BHCS Cash balance.</li>
            <li>Calculate the difference: <em>invoice total &minus; BHCS Cash balance</em>.</li>
            <li>Send that exact difference as a Zelle payment using your invoice memo code.</li>
            <li>Once the school confirms your Zelle payment, return to the invoice.</li>
            <li>Select <strong>BHCS Cash</strong> as the payment method &mdash; your balance now covers the remaining amount in full.</li>
          </ol>
          <p>
            This approach lets you use BHCS Cash even when you prefer Zelle over cards or bank transfers.
          </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <div className="text-gray-700 leading-relaxed text-sm space-y-2">
              <p>Yes &mdash; here is how:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Open the invoice and note the total due and your current BHCS Cash balance.</li>
                <li>Calculate the difference: <em>invoice total &minus; BHCS Cash balance</em>.</li>
                <li>Send that exact difference as a Zelle payment using your invoice memo code.</li>
                <li>Once we confirm your Zelle payment, return to the invoice.</li>
                <li>Select <strong>BHCS Cash</strong> as the payment method &mdash; your balance now covers the remaining amount in full.</li>
              </ol>
              <p>
                This approach lets you use BHCS Cash even when you prefer Zelle over cards or bank transfers.
              </p>
            </div>
          ),
        },
      },
      color: 'blue'
    },
    {
      id: 28,
      category: 'BHCS Cash',
      question: 'Can I set up autopay using BHCS Cash?',
      questionZh: '可以用 BHCS Cash 设置自动付款吗？',
      answerZh: (
        <p className="text-gray-700 leading-relaxed text-sm">
          可以。在 BHCS Cash 偏好设置中，您可以启用自动付款。余额会在账单到期日自动抵扣。您还可以设置备用支付方式，用于支付余额未覆盖的部分。
        </p>
      ),
      answer: (
        <p className="text-gray-700 leading-relaxed text-sm">
          Yes. In your BHCS Cash preferences, you can enable autopay. Your balance will be applied automatically on the invoice due date. You can also set a fallback payment method for any amount not covered by your balance.
        </p>
      ),
      color: 'blue'
    },
    {
      id: 29,
      category: 'BHCS Cash',
      question: 'Can I get my money back?',
      questionZh: '我可以申请退款吗？',
      answerZh: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            可以，您的可提现余额可退回原支付方式。如需申请提现，请发送邮件至{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">billing@bhcskids.com</a>，
            说明您希望退回的金额及首选退款方式。
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            提现由学校管理员处理。不可提现额度不予退款。
          </p>
        </div>
      ),
      answer: (
        <div>
          <p className="text-gray-700 leading-relaxed mb-3 text-sm">
            Yes, your withdrawable balance can be returned to your original payment method. To request a withdrawal, email the school at{' '}
            <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">billing@bhcskids.com</a>{' '}
            with the amount you&apos;d like returned and your preferred method.
          </p>
          <p className="text-gray-700 leading-relaxed text-sm">
            Withdrawals are processed by the school admin. Non-withdrawable credits cannot be refunded.
          </p>
        </div>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <div>
              <p className="text-gray-700 leading-relaxed mb-3 text-sm">
                Yes, your withdrawable balance can be returned to your original payment method. To request a withdrawal, email us at{' '}
                <a href="mailto:billing@bhcskids.com" className="text-blue-600 hover:text-blue-800 underline font-medium">billing@bhcskids.com</a>{' '}
                with the amount you&apos;d like returned and your preferred method.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                Withdrawals are processed by our admin team. Non-withdrawable credits cannot be refunded.
              </p>
            </div>
          ),
        },
      },
      color: 'purple'
    },
    {
      id: 30,
      category: 'BHCS Cash',
      question: 'What happens to my BHCS Cash balance if I cancel an enrollment?',
      questionZh: '如果我取消报名，我的 BHCS Cash 余额会怎样？',
      answerZh: (
        <p className="text-gray-700 leading-relaxed text-sm">
          如您使用 BHCS Cash 支付了某项报名，退款金额会退回您的 BHCS Cash 账户（退回原账户类别），并须遵循该课程的取消及退款政策。该金额不会自动退回您的银行卡。
        </p>
      ),
      answer: (
        <p className="text-gray-700 leading-relaxed text-sm">
          If you paid for an enrollment using BHCS Cash, the refunded amount returns to your BHCS Cash account (same bucket it came from), subject to the program&apos;s cancellation and refund policy. It is not automatically sent back to your card.
        </p>
      ),
      color: 'rose'
    },
    {
      id: 31,
      category: 'BHCS Cash',
      question: 'Is my BHCS Cash secure?',
      questionZh: '我的 BHCS Cash 安全吗？',
      answerZh: (
        <p className="text-gray-700 leading-relaxed text-sm">
          安全。您的 BHCS Cash 与您的 BHCS 账户绑定，受您的登录凭据和 Google 登录保护。BHCS 不是银行，BHCS Cash 不受 FDIC 保险保障。如发现任何未经授权的活动，请立即告知学校。
        </p>
      ),
      answer: (
        <p className="text-gray-700 leading-relaxed text-sm">
          Yes. Your BHCS Cash is tied to your BHCS account, which is protected by your login credentials and Google sign-in. BHCS is not a bank and BHCS Cash is not FDIC-insured. Report any unauthorized activity to the school immediately.
        </p>
      ),
      byBrand: {
        kaleido: {
          answer: (
            <p className="text-gray-700 leading-relaxed text-sm">
              Yes. Your BHCS Cash is tied to your portal account, which is protected by your login credentials and Google sign-in. We are not a bank, and BHCS Cash is not FDIC-insured. Report any unauthorized activity to us immediately.
            </p>
          ),
        },
      },
      color: 'green'
    },
    {
      id: 32,
      category: 'BHCS Cash',
      question: 'Can I use BHCS Cash for any payment at BHCS?',
      questionZh: 'BHCS Cash 可以用于在 BHCS 的任何付款吗？',
      answerZh: (
        <p className="text-gray-700 leading-relaxed text-sm">
          可以——BHCS Cash 可用于 BHCS 生成的任何账单，包括月度学费、夏令营报名和各类附加项目。
        </p>
      ),
      answer: (
        <p className="text-gray-700 leading-relaxed text-sm">
          Yes &mdash; BHCS Cash can be used for any invoice generated by BHCS, including monthly tuition, camp enrollments, and add-ons.
        </p>
      ),
      byBrand: {
        kaleido: {
          question: 'Can I use BHCS Cash for any payment in the portal?',
          answer: (
            <p className="text-gray-700 leading-relaxed text-sm">
              Yes &mdash; BHCS Cash can be used for any invoice in the portal, whether it is for a Kaleido program or a Bright Horizon Chinese School one.
            </p>
          ),
        },
      },
      color: 'yellow'
    },
  ], []);

  const faqData = useMemo(
    () => faqsForBrand(allFaqData, activeBrand, isMultiBrand),
    [allFaqData, activeBrand, isMultiBrand],
  );

  // Filter FAQ items based on search query
  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return faqData;
    
    const query = searchQuery.toLowerCase();
    const rawQuery = searchQuery.trim();
    return faqData.filter(faq => {
      const answerText = typeof faq.answer === 'string' ? faq.answer : '';
      return faq.question.toLowerCase().includes(query) ||
             (faq.questionZh ?? '').includes(rawQuery) ||
             answerText.toLowerCase().includes(query) ||
             faq.category.toLowerCase().includes(query) ||
             (CATEGORY_ZH[faq.category] ?? '').includes(rawQuery);
    });
  }, [faqData, searchQuery]);

  // Group filtered FAQs by category
  const groupedFaqs = useMemo(() => {
    const groups: Record<string, FAQItem[]> = {};
    filteredFaqs.forEach(faq => {
      if (!groups[faq.category]) {
        groups[faq.category] = [];
      }
      groups[faq.category].push(faq);
    });
    return groups;
  }, [filteredFaqs]);

  // Helper functions for category icons and colors
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Registration':
        return (
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Payment':
        return (
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      case 'Programs':
        return (
          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
      case 'Program Profiles':
        return (
          <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        );
      case 'BHCS Cash':
        return (
          <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getCategoryIconColor = (category: string) => {
    switch (category) {
      case 'Registration':
        return 'bg-green-100';
      case 'Payment':
        return 'bg-blue-100';
      case 'Programs':
        return 'bg-purple-100';
      case 'Program Profiles':
        return 'bg-indigo-100';
      case 'BHCS Cash':
        return 'bg-emerald-100';
      default:
        return 'bg-gray-100';
    }
  };



  const getBadgeColor = (color: string) => {
    switch (color) {
      case 'blue':
        return 'bg-blue-500';
      case 'yellow':
        return 'bg-yellow-500';
      case 'green':
        return 'bg-green-500';
      case 'purple':
        return 'bg-purple-500';
      case 'indigo':
        return 'bg-indigo-500';
      case 'emerald':
        return 'bg-emerald-500';
      case 'amber':
        return 'bg-amber-500';
      case 'red':
        return 'bg-red-500';
      case 'cyan':
        return 'bg-cyan-500';
      case 'orange':
        return 'bg-orange-500';
      case 'teal':
        return 'bg-teal-500';
      case 'rose':
        return 'bg-rose-500';
      default:
        return 'bg-gray-500';
    }
  };

  const toggleExpanded = (faqId: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedItems(newExpanded);
  };

  const expandAll = () => {
    setExpandedItems(new Set(faqData.map(faq => faq.id)));
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  // Helper function to generate FAQ links
  const getFaqLink = (faqId: number) => {
    return `${window.location.origin}${window.location.pathname}#faq-${faqId}`;
  };

  // Copy FAQ link to clipboard
  const copyFaqLink = async (faqId: number) => {
    try {
      const link = getFaqLink(faqId);
      await navigator.clipboard.writeText(link);
      // You could add a toast notification here
      console.log('FAQ link copied to clipboard:', link);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-4 sm:py-6 px-3 sm:px-4 lg:px-8">
      <div className="max-w-5xl mx-auto">

        {/* AI Assistant entrance — prominent CTA into the live chat. Shown only
            when the Parent Genie flag is on; otherwise this is a plain FAQ page. */}
        {(
          <Link
            to="/assistant"
            className="group block mb-4 sm:mb-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-4 sm:px-6 sm:py-5 shadow-md hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-shrink-0 h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-base sm:text-lg leading-tight">
                  {bi('问问 AI 助手', 'Ask the AI Assistant')}
                </div>
                <div className="text-sm text-blue-100 mt-0.5">
                  {bi('即时获得关于您账户的答案', 'Instant answers about your account')}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        )}

        {/* Search Bar */}
        <div className="mb-4 sm:mb-6">
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder={bilingual ? '' : SEARCH_PLACEHOLDER_EN}
              aria-label={bilingual ? `${SEARCH_PLACEHOLDER_ZH} / ${SEARCH_PLACEHOLDER_EN}` : SEARCH_PLACEHOLDER_EN}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
            />
            {showAnimatedPlaceholder && (
              <div
                aria-hidden="true"
                className={`absolute inset-y-0 left-10 right-3 flex items-center pointer-events-none text-gray-500 transition-opacity duration-300 ${phVisible ? 'opacity-100' : 'opacity-0'}`}
              >
                <span className="truncate">
                  {phLang === 'zh' ? SEARCH_PLACEHOLDER_ZH : SEARCH_PLACEHOLDER_EN}
                </span>
              </div>
            )}
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="text-center mt-2">
              <span className="text-sm text-gray-600">
                {bilingual
                  ? `找到 ${filteredFaqs.length} 条与“${searchQuery}”相关的结果`
                  : `Found ${filteredFaqs.length} result${filteredFaqs.length !== 1 ? 's' : ''} for "${searchQuery}"`}
              </span>
            </div>
          )}
          
          {/* Expand/Collapse All Buttons */}
          <div className="flex justify-center gap-1 mt-2">
            <button
              onClick={expandAll}
              className="inline-flex items-center px-2 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              {bi('展开全部', 'Expand All')}
            </button>
            <button
              onClick={collapseAll}
              className="inline-flex items-center px-2 py-1 bg-gray-600 text-white rounded text-xs font-medium hover:bg-gray-700 transition-colors"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              {bi('收起全部', 'Collapse All')}
            </button>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-8">
          {Object.keys(groupedFaqs).length === 0 && searchQuery ? (
            <div className="text-center py-8 sm:py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">{bi('未找到结果', 'No results found')}</h3>
              <p className="text-gray-600">{bi('请尝试其他关键词，或浏览下方全部问题。', 'Try searching with different keywords or browse all questions below.')}</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {bi('清除搜索', 'Clear Search')}
              </button>
            </div>
          ) : (
            Object.entries(groupedFaqs).map(([category, faqs]) => (
              <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center p-4 sm:p-6 border-b border-gray-200">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 ${getCategoryIconColor(category)} rounded-lg flex items-center justify-center mr-3 sm:mr-4`}>
                    {getCategoryIcon(category)}
                  </div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                    {bilingual && CATEGORY_ZH[category]
                      ? `关于${CATEGORY_ZH[category]} About ${category}`
                      : `About ${category}`}
                  </h2>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {faqs.map((faq) => {
                    const badgeColor = getBadgeColor(faq.color);
                    const isExpanded = expandedItems.has(faq.id);
                    
                    return (
                      <div 
                        key={faq.id} 
                        id={`faq-${faq.id}`}
                        className="bg-white hover:bg-gray-50 transition-colors"
                      >
                        {/* Clickable Header */}
                        <button
                          onClick={() => toggleExpanded(faq.id)}
                          className="w-full p-4 sm:p-6 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-start">
                              {/* A dot, not a number: `id` is the permanent #faq-N link target, and
                                  a brand that hides questions would show gaps in it. */}
                              <div
                                aria-hidden="true"
                                className={`flex-shrink-0 w-2 h-2 ${badgeColor} rounded-full mr-3 sm:mr-4 mt-1.5 sm:mt-2`}
                              />
                              <div className="flex-1">
                                {bilingual && faq.questionZh ? (
                                  <>
                                    <h3 className="text-sm sm:text-base font-medium text-gray-900 pr-2 sm:pr-4">{faq.questionZh}</h3>
                                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5 pr-2 sm:pr-4">{faq.question}</p>
                                  </>
                                ) : (
                                  <h3 className="text-sm sm:text-base font-medium text-gray-900 pr-2 sm:pr-4">{faq.question}</h3>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {/* Bookmark/Copy Link Button */}
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyFaqLink(faq.id);
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                                title="Copy link to this question"
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyFaqLink(faq.id);
                                  }
                                }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                              </div>
                              {/* Expand/Collapse Icon */}
                              <svg 
                                className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-600 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </button>
                        
                        {/* Expandable Content */}
                        {isExpanded && (
                          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                            {bilingual && faq.answerZh ? (
                              <>
                                <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                  {typeof faq.answerZh === 'string' ? (
                                    <p>{faq.answerZh}</p>
                                  ) : (
                                    faq.answerZh
                                  )}
                                </div>
                                <div className="my-3 border-t border-dashed border-gray-200" />
                                <div className="text-gray-500 leading-relaxed text-sm">
                                  {typeof faq.answer === 'string' ? (
                                    <p>{faq.answer}</p>
                                  ) : (
                                    faq.answer
                                  )}
                                </div>
                              </>
                            ) : (
                              <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                {typeof faq.answer === 'string' ? (
                                  <p>{faq.answer}</p>
                                ) : (
                                  faq.answer
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          {/* Contact Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 text-center">{bi('需要更多帮助？', 'Need More Help?')}</h3>
            </div>
            <div className="p-4 sm:p-6">
              <p className="text-gray-600 mb-4 text-center">
                {bi('如果您还有本页未涵盖的问题，请联系我们：', 'If you have additional questions not covered in this FAQ, please contact us:')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
                <a href="mailto:billing@bhcskids.com" className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {bi('邮件联系', 'Email Us')}
                </a>
                <a href="tel:650-620-0299" className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm sm:text-base">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {bi('电话联系', 'Call Us')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Utility function to generate FAQ links for use in other components
export const getFaqLink = (faqId: number, baseUrl?: string) => {
  const url = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${url}/faq#faq-${faqId}`;
};

// FAQ ID constants for easy reference in other components
export const FAQ_IDS = {
  REGISTRATION_EMAIL: 1,
  CONFIRMATION_EMAIL: 2,
  ADD_STUDENT: 3,
  SECOND_GUARDIAN: 4,
  UNENROLL_PROCESS: 5,
  ZELLE_LIMITS: 6,
  ACH_PENDING: 7,
  AUTO_PAY_SETUP: 12,
  BUNDLE_PAYMENTS: 8,
  PAYMENT_HISTORY: 9,
  ZELLE_PAYMENT_INSTRUCTIONS: 10,
  ACH_LOAD_FAILED: 11,
  FLEX_PROGRAM: 13,
  FLEX_PLAN_EDIT: 14,
  FLEX_ABSENCES: 15,
  AFTERSCHOOL_HOURS: 16,
  TUITION_PRORATION: 17,
  NON_SMFC_CALENDAR: 18,
  SELF_TRANSPORT: 19,
  WITHDRAWAL_REFUND: 20,
  PROGRAM_LOCATIONS: 36,
  PROGRAM_PROFILES: 33,
  PROFILE_MISSING_ITEMS: 34,
  PROFILE_AUTO_SWITCH: 35,
} as const;

export default FAQ; 